import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from '@/components/breathing/StepperInput';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Settings, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/utils/constants';

const PatternsPage = () => {
  const [patterns, setPatterns] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patternToDelete, setPatternToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newPattern, setNewPattern] = useState({
    name: '',
    description: '',
    inhale_duration: 4,
    hold_after_inhale: 4,
    exhale_duration: 4,
    hold_after_exhale: 4
  });

  const fetchPatterns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patterns`);
      setPatterns(response.data);
    } catch {
      toast.error("Échec du chargement des exercices");
    }
  }, []);

  useEffect(() => {
    fetchPatterns();
  }, [fetchPatterns]);

  const handleCreatePattern = async () => {
    if (!newPattern.name.trim()) {
      toast.error("Veuillez entrer un nom pour l'exercice");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/patterns`, newPattern);
      if (response.data) {
        toast.success("Exercice créé avec succès !");
        setDialogOpen(false);
        setNewPattern({
          name: '',
          description: '',
          inhale_duration: 4,
          hold_after_inhale: 4,
          exhale_duration: 4,
          hold_after_exhale: 4
        });
        fetchPatterns();
      }
    } catch (error) {
      console.error("Error creating pattern:", error);
      toast.error(error.response?.data?.detail || "Échec de la création de l'exercice");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePattern = async (id) => {
    try {
      await axios.delete(`${API}/patterns/${id}`);
      toast.success("Exercice supprimé");
      setDeleteDialogOpen(false);
      setPatternToDelete(null);
      fetchPatterns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de supprimer cet exercice");
    }
  };

  const openDeleteDialog = (pattern) => {
    setPatternToDelete(pattern);
    setDeleteDialogOpen(true);
  };

  const presetPatterns = patterns.filter(p => p.is_preset);
  const customPatterns = patterns.filter(p => !p.is_preset);

  // Calculate total cycle duration
  const cycleDuration = newPattern.inhale_duration + newPattern.hold_after_inhale + 
                        newPattern.exhale_duration + newPattern.hold_after_exhale;

  return (
    <div 
      className="min-h-screen pb-24" 
      data-testid="patterns-page"
      style={{
        backgroundImage: 'url(/images/history-patterns-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-md mx-auto px-6 pt-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Exercices de Respiration
          </h1>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-cyan-500 hover:bg-cyan-600" size="sm" data-testid="create-pattern-btn">
                <Plus size={18} className="mr-1" /> Créer
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-sm mx-auto">
              <DialogHeader>
                <DialogTitle>Créer un exercice personnalisé</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Nom de l&apos;exercice</Label>
                  <Input
                    value={newPattern.name}
                    onChange={(e) => setNewPattern({ ...newPattern, name: e.target.value })}
                    placeholder="Mon exercice de respiration"
                    className="mt-1"
                    data-testid="pattern-name-input"
                  />
                </div>
                <div>
                  <Label>Description (optionnel)</Label>
                  <Input
                    value={newPattern.description}
                    onChange={(e) => setNewPattern({ ...newPattern, description: e.target.value })}
                    placeholder="Décrivez votre exercice"
                    className="mt-1"
                    data-testid="pattern-desc-input"
                  />
                </div>
                <div>
                  <Label>Inspiration</Label>
                  <div className="mt-2">
                    <StepperInput
                      value={newPattern.inhale_duration}
                      onChange={(v) => setNewPattern({ ...newPattern, inhale_duration: v })}
                      min={1}
                      max={10}
                      step={1}
                      unit="sec"
                      label="inspiration"
                      colorClass="sky"
                      valueTestId="inhale-value"
                      incTestId="inhale-inc"
                      decTestId="inhale-dec"
                    />
                  </div>
                </div>
                <div>
                  <Label>Rétention (poumons pleins)</Label>
                  <div className="mt-2">
                    <StepperInput
                      value={newPattern.hold_after_inhale}
                      onChange={(v) => setNewPattern({ ...newPattern, hold_after_inhale: v })}
                      min={0}
                      max={10}
                      step={1}
                      unit="sec"
                      label="rétention pleine"
                      colorClass="amber"
                      valueTestId="hold-in-value"
                      incTestId="hold-in-inc"
                      decTestId="hold-in-dec"
                    />
                  </div>
                </div>
                <div>
                  <Label>Expiration</Label>
                  <div className="mt-2">
                    <StepperInput
                      value={newPattern.exhale_duration}
                      onChange={(v) => setNewPattern({ ...newPattern, exhale_duration: v })}
                      min={1}
                      max={10}
                      step={1}
                      unit="sec"
                      label="expiration"
                      colorClass="rose"
                      valueTestId="exhale-value"
                      incTestId="exhale-inc"
                      decTestId="exhale-dec"
                    />
                  </div>
                </div>
                <div>
                  <Label>Pause (poumons vides)</Label>
                  <div className="mt-2">
                    <StepperInput
                      value={newPattern.hold_after_exhale}
                      onChange={(v) => setNewPattern({ ...newPattern, hold_after_exhale: v })}
                      min={0}
                      max={10}
                      step={1}
                      unit="sec"
                      label="pause vide"
                      colorClass="violet"
                      valueTestId="hold-out-value"
                      incTestId="hold-out-inc"
                      decTestId="hold-out-dec"
                    />
                  </div>
                </div>
                <div className="bg-teal-50 rounded-lg p-3 text-center">
                  <p className="text-sm text-teal-700">
                    Durée d&apos;un cycle : <span className="font-bold">{cycleDuration}s</span>
                  </p>
                </div>
                <Button 
                  onClick={handleCreatePattern} 
                  disabled={loading}
                  className="w-full bg-cyan-500 hover:bg-cyan-600" 
                  data-testid="save-pattern-btn"
                >
                  {loading ? "Création..." : "Créer l'exercice"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="presets" className="w-full">
          <TabsList className="w-full bg-white/70 backdrop-blur mb-4">
            <TabsTrigger value="presets" className="flex-1" data-testid="tab-presets">Prédéfinis</TabsTrigger>
            <TabsTrigger value="custom" className="flex-1" data-testid="tab-custom">Personnalisés</TabsTrigger>
          </TabsList>
          
          <TabsContent value="presets">
            <div className="space-y-3">
              {presetPatterns.map((pattern) => (
                <Card key={pattern.id} className="bg-white/70 backdrop-blur border-teal-100" data-testid={`preset-pattern-${pattern.id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-slate-700">{pattern.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-500 mb-3">{pattern.description}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-teal-600">
                      <span className="bg-teal-50 px-2 py-1 rounded">Inspir: {pattern.inhale_duration}s</span>
                      {pattern.hold_after_inhale > 0 && <span className="bg-teal-50 px-2 py-1 rounded">Rétention: {pattern.hold_after_inhale}s</span>}
                      <span className="bg-teal-50 px-2 py-1 rounded">Expir: {pattern.exhale_duration}s</span>
                      {pattern.hold_after_exhale > 0 && <span className="bg-teal-50 px-2 py-1 rounded">Pause: {pattern.hold_after_exhale}s</span>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="custom">
            {customPatterns.length === 0 ? (
              <Card className="bg-white/70 backdrop-blur border-teal-100">
                <CardContent className="py-8 text-center">
                  <Settings className="mx-auto mb-3 text-teal-300" size={40} />
                  <p className="text-slate-500">Aucun exercice personnalisé</p>
                  <p className="text-sm text-slate-400 mt-1">Créez votre propre rythme de respiration</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {customPatterns.map((pattern) => (
                  <Card key={pattern.id} className="bg-white/70 backdrop-blur border-teal-100" data-testid={`custom-pattern-${pattern.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-base text-slate-700">{pattern.name}</CardTitle>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(pattern)}
                          className="text-rose-400 hover:text-rose-500 hover:bg-rose-50 -mt-1"
                          data-testid={`delete-pattern-${pattern.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-500 mb-3">{pattern.description}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-teal-600">
                        <span className="bg-teal-50 px-2 py-1 rounded">Inspir: {pattern.inhale_duration}s</span>
                        {pattern.hold_after_inhale > 0 && <span className="bg-teal-50 px-2 py-1 rounded">Rétention: {pattern.hold_after_inhale}s</span>}
                        <span className="bg-teal-50 px-2 py-1 rounded">Expir: {pattern.exhale_duration}s</span>
                        {pattern.hold_after_exhale > 0 && <span className="bg-teal-50 px-2 py-1 rounded">Pause: {pattern.hold_after_exhale}s</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-white max-w-sm mx-auto">
            <DialogHeader>
              <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-rose-600" />
              </div>
              <DialogTitle className="text-center">Supprimer cet exercice ?</DialogTitle>
              <DialogDescription className="text-center">
                Êtes-vous sûr de vouloir supprimer <strong>&quot;{patternToDelete?.name}&quot;</strong> ? Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:justify-center">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDeletePattern(patternToDelete?.id)}
                className="bg-rose-500 hover:bg-rose-600"
              >
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PatternsPage;
